import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  formatRxSelectorLabel,
  rxStatusLabel,
  sortRxHistory,
} from '../../../../constants/rx-names';
import { RxCompareDialogComponent } from './rx-compare-dialog.component';

@Component({
  selector: 'app-rx-history-dialog',
  template: `
    <h2 mat-dialog-title>VIEW RX HISTORY</h2>
    <mat-dialog-content>
      <p class="rx-history-message">
        Usable active prescriptions appear first. Voided prescriptions stay
        below them in gray and cannot be used on a new job. Expired
        prescriptions are listed separately for reference. Select up to four
        rows and Compare to review them together.
      </p>
      <table class="rx-history-table" *ngIf="rows.length">
        <thead>
          <tr>
            <th></th>
            <th>RX NAME</th>
            <th>EXAM DATE</th>
            <th>EXPIRES</th>
            <th>STATUS</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let row of rows"
            (click)="select(row.rx)"
            [class.rx-history-hidden]="row.status === 'HIDDEN'"
            [class.rx-history-voided]="row.status === 'VOIDED'"
            [class.rx-history-expired]="row.status === 'EXPIRED'"
          >
            <td (click)="$event.stopPropagation()">
              <mat-checkbox
                [checked]="isSelected(row.rx)"
                (change)="toggleCompare(row.rx, $event.checked)"
              ></mat-checkbox>
            </td>
            <td>{{ row.name }}</td>
            <td>{{ row.exam }}</td>
            <td>{{ row.expires }}</td>
            <td>{{ row.status }}</td>
          </tr>
        </tbody>
      </table>
      <p *ngIf="!rows.length">No prescription history for this patient.</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button
        mat-stroked-button
        type="button"
        [disabled]="selected.length < 2"
        (click)="compare()"
      >
        COMPARE
      </button>
      <button mat-button type="button" (click)="dialogRef.close()">CLOSE</button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .rx-history-message {
        margin: 0 0 12px;
        font-size: 13px;
        line-height: 1.4;
      }
      .rx-history-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }
      .rx-history-table th,
      .rx-history-table td {
        text-align: left;
        padding: 8px 6px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }
      .rx-history-table tbody tr {
        cursor: pointer;
      }
      .rx-history-table tbody tr:hover {
        background: rgba(0, 0, 0, 0.04);
      }
      .rx-history-hidden td,
      .rx-history-voided td,
      .rx-history-expired td {
        color: rgba(0, 0, 0, 0.54);
      }
      .rx-history-voided td {
        font-style: italic;
      }
    `,
  ],
})
export class RxHistoryDialogComponent {
  rows: Array<{
    rx: any;
    name: string;
    exam: string;
    expires: string;
    status: string;
  }> = [];
  selected: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<RxHistoryDialogComponent, any>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: { prescriptions?: any[] }
  ) {
    this.rows = sortRxHistory(data?.prescriptions || []).map((rx) => ({
      rx,
      name: formatRxSelectorLabel(rx),
      exam: this.shortDate(rx.examDate),
      expires: this.shortDate(rx.expirationDate),
      status: rxStatusLabel(rx),
    }));
  }

  isSelected(rx: any): boolean {
    return this.selected.some((item) => String(item._id) === String(rx._id));
  }

  toggleCompare(rx: any, checked: boolean): void {
    if (checked) {
      if (this.selected.length >= 4) {
        this.selected.shift();
      }
      this.selected.push(rx);
      return;
    }
    this.selected = this.selected.filter(
      (item) => String(item._id) !== String(rx._id)
    );
  }

  compare(): void {
    if (this.selected.length < 2) {
      return;
    }
    this.dialog.open(RxCompareDialogComponent, {
      width: this.selected.length > 2 ? '1100px' : '960px',
      data: { prescriptions: this.selected.slice(0, 4) },
    });
  }

  select(rx: any): void {
    const status = rxStatusLabel(rx);
    if (status === 'VOIDED' || status === 'EXPIRED') {
      return;
    }
    this.dialogRef.close(rx);
  }

  private shortDate(value?: string | Date): string {
    if (!value) {
      return '—';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return '—';
    }
    return `${date.getMonth() + 1}/${date.getDate()}/${String(
      date.getFullYear()
    ).slice(-2)}`;
  }
}
